import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import LogoComponent from './logo';

const Navbar = () => {
  const [activeItem, setActiveItem] = useState('Home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const navItems = [
    { label: 'Home', path: '/', sectionId: null },
    { 
      label: 'About', 
      subItems: [
        { label: 'Why Choose Us', sectionId: 'whychoose' },
        { label: 'Our Team', sectionId: 'team' }
      ]
    },
    { label: 'Application Process', sectionId: 'applicationprocess' },
    { label: 'Apply', path: '/Apply', sectionId: null, cta: true }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path, sectionId) => {
    if (path) {
      navigate(path);
      if (path === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
    } 
    else if (sectionId) {
      const el = document.getElementById(sectionId);
      el?.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveItem(path || sectionId);
    setMobileMenuOpen(false);
  };

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-white/90 backdrop-blur-sm py-4'}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
         <div 
  className="flex items-center cursor-pointer"
  onClick={() => handleNavigation('/', null)}
>
  {/* Logo container with fixed dimensions */}
<div 
  className="flex items-center cursor-pointer"
  onClick={() => handleNavigation('/', null)}
>
  {/* Logo with exact dimensions */}
  <div className="w-[40px] h-[40px] min-w-[40px] mr-3">
    <LogoComponent className="w-full h-full" />
  </div>
  
  {/* Text with pixel-perfect alignment */}
  <div className="hidden sm:block pt-[2px]">
    <h1 className="text-[17px] font-bold text-green-800 leading-[1.1] tracking-tight">RIGHT TIME MEDICALS</h1>
    <p className="text-[11px] text-gray-600 mt-[3px] font-medium">Always on time...</p>
  </div>
</div>
</div>
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <div key={item.label} className="relative group">
                {item.subItems ? (
                  <>
                    <button className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeItem === item.label ? 'text-green-800' : 'text-gray-700 hover:text-green-700'
                    }`}>
                      {item.label}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                    <div className="absolute left-0 mt-2 w-56 origin-top-left rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {item.subItems.map((subItem) => (
                        <button
                          key={subItem.label}
                          onClick={() => handleNavigation(null, subItem.sectionId)}
                          className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 hover:text-green-700 rounded-md"
                        >
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => handleNavigation(item.path, item.sectionId)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeItem === item.label 
                        ? 'text-green-800' 
                        : item.cta 
                          ? 'bg-green-600 text-white hover:bg-green-700' 
                          : 'text-gray-700 hover:text-green-700'
                    }`}
                  >
                    {item.label}
                  </button>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-2">
            {navItems.map((item) => (
              <div key={item.label}>
                {item.subItems ? (
                  <details className="group">
                    <summary className="flex justify-between items-center px-4 py-2 w-full text-left text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer">
                      {item.label}
                      <ChevronDown className="h-4 w-4 group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="ml-4 mt-1 space-y-1">
                      {item.subItems.map((subItem) => (
                        <button
                          key={subItem.label}
                          onClick={() => handleNavigation(null, subItem.sectionId)}
                          className="block w-full px-4 py-2 text-left text-gray-600 rounded-md hover:bg-gray-50"
                        >
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  </details>
                ) : (
                  <button
                    onClick={() => handleNavigation(item.path, item.sectionId)}
                    className={`w-full px-4 py-2 text-left rounded-lg ${
                      item.cta 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;